import { en } from '../i18n/en';
import { es, type MessageKey, type Messages } from '../i18n/es';

/**
 * Dos idiomas y unas cincuenta cadenas: no hace falta una librería de i18n, y
 * RNF-10 penaliza cada kilobyte del bundle.
 */

export type Locale = 'es' | 'en';

const DICTIONARIES: Record<Locale, Messages> = { es, en };

/** El español es la reserva: cualquier idioma que no sea inglés cae ahí. */
export function resolveLocale(language: string | undefined): Locale {
  return language?.toLowerCase().startsWith('en') ? 'en' : 'es';
}

export function getMessages(locale: Locale): Messages {
  return DICTIONARIES[locale];
}

/** Idioma del navegador, con reserva segura fuera del navegador. */
export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return 'es';
  return resolveLocale(navigator.language);
}

/** Sustituye `{clave}` por su valor. Lo que no se pasa se deja tal cual. */
export function format(template: string, values: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

export function createTranslator(locale: Locale) {
  const messages = getMessages(locale);

  return (key: MessageKey, values?: Record<string, string | number>): string =>
    format(messages[key], values);
}

export type Translator = ReturnType<typeof createTranslator>;

/**
 * Traductor de la sesión. El idioma se resuelve una sola vez al cargar: no hay
 * selector de idioma, así que no hace falta un contexto de React.
 */
export const t: Translator = createTranslator(detectLocale());
